import { Button, Card, CardContent, Dialog, FormControl, Grid, IconButton, InputLabel, MenuItem, Select, TextField, Typography } from "@material-ui/core";
import { useContext, useEffect } from "react";
import { CalculationVisualizerContext } from "../../../Context/CalculationVisualizerContextProvider";
import { useState } from "react";
import { getOptionAttributes, updateCalcGraph } from "../../../Actions";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import { Delete } from "@material-ui/icons";

function OperandInput(props) {
  const operandType = props.operand?.type || "manual";

  const updateOperand = (value, attribute, formula, inputAttribute, operandType) => {
    const tempOperand = { ...props.operand };
    const operandTypeForCheck = operandType || tempOperand.type;

    if (operandType && operandTypeForCheck !== tempOperand.type) {
      delete tempOperand.value;
      delete tempOperand.attribute;
      delete tempOperand.formula;
      delete tempOperand.inputAttribute;
    }

    tempOperand.value = value ?? tempOperand.value ?? undefined;
    tempOperand.attribute = attribute || tempOperand.attribute || undefined;
    tempOperand.formula = formula || tempOperand.formula || undefined;
    tempOperand.inputAttribute = inputAttribute || tempOperand.inputAttribute || undefined;
    tempOperand.type = operandTypeForCheck || "manual";

    props.updateOperand({ ...tempOperand });
  }

  return (
    <Grid 
      container 
      spacing={2} 
      style={{ 
        marginTop: props.nodeType === 'condition' ? "10px" : "0px", 
        marginBottom: props.nodeType === 'condition' ? "10px" : "0px",
        border: props.nodeType === 'condition' ? "2px solid #00000017" : "0px", 
        borderRadius: "5px" 
      }}
    >
      <Grid item xs={10}>
        <ToggleButtonGroup
          value={props.operand?.type || "manual"}
          exclusive
          size='small'
          onChange={(e, newValue) => {
            if (!newValue) return;

            updateOperand(undefined, undefined, undefined, undefined, newValue);
          }}
          aria-label="text alignment"
          style={{display: "inline-block"}}
        >
          <ToggleButton value='manual' aria-label="manual value" style={operandType === "manual" ? {backgroundColor: "#142E3E", color: "#28C4FC"} : {backgroundColor: "#00000017", color: "initial"}}>
            Manual Value
          </ToggleButton>
          <ToggleButton value='attribute' aria-label="attribute value" style={operandType === "attribute" ? {backgroundColor: "#142E3E", color: "#28C4FC"} : {backgroundColor: "#00000017", color: "initial"}}>
            Attribute
          </ToggleButton>
          { props.nodeType === "condition" && (
            <ToggleButton value='formula' aria-label="formula" style={operandType === "formula" ? {backgroundColor: "#142E3E", color: "#28C4FC"} : {backgroundColor: "#00000017", color: "initial"}}>
              Formula
            </ToggleButton>
          )}
        </ToggleButtonGroup>
      </Grid>
      { props.nodeType === "condition" && props.canDelete ? (
          <Grid item xs={2} style={{ textAlign: "right" }}>
            <IconButton style={{color: "#f44336"}} onClick={() => props.deleteOperand()}><Delete /></IconButton>
          </Grid>
        ) : undefined 
      }
      { ["attribute", "formula"].includes(operandType) ? ( 
          <Grid item xs={12}>
            <FormControl fullWidth size="small" variant="filled">
              <InputLabel shrink id="option-list-label">Selected Option Attribute</InputLabel>
              <Select
                value={props.operand?.attribute || ""}
                displayEmpty
                id="option-list-label"
                onChange={(e) => updateOperand(undefined, e.target.value, undefined, undefined)}
              >
                <MenuItem value="" disabled>Please select one of the following</MenuItem>
                { 
                  props.attributes.map((a) => {
                    return <MenuItem key={a} value={a}>{a}</MenuItem>
                  })
                }
              </Select>
            </FormControl>
          </Grid>
        ) : undefined
      }
      { ["manual", "formula"].includes(operandType) ? (
          <Grid item xs={12}>
            { props.nodeType === "condition" ? (
              <TextField
                label="Manual Value"
                fullWidth
                size="small"
                variant="filled"
                value={props.operand?.value || ""}
                onChange={(e) => updateOperand(e.target.value, undefined, undefined, undefined)}
              />
            ) : (
              <TextField
                label="Manual Value"
                fullWidth
                size="small"
                type="number"
                inputProps={{ min: 0 }}
                variant="filled"
                value={props.operand?.value || ""}
                error={Number(props.operand?.value) < 0}
                helperText={Number(props.operand?.value) < 0 ? "Value must be greater than 0" : ""}
                onChange={(e) => updateOperand(e.target.value, undefined, undefined, undefined)}
              />
            )}
          </Grid> 
        ) : undefined 
      }
      { ["formula"].includes(operandType) ? (
          <>
            <Grid item xs={12}>
              <FormControl fullWidth size="small" variant="filled">
                <InputLabel shrink id="option-list-label">Selected Input Attribute</InputLabel>
                <Select
                  value={props.operand?.inputAttribute ?? ""}
                  displayEmpty
                  id="option-list-label"
                  onChange={(e) => updateOperand(undefined, undefined, undefined, e.target.value)}
                >
                  <MenuItem value="" disabled>Please select one of the following</MenuItem>
                  <MenuItem value="current">Current</MenuItem>
                  <MenuItem value="price">Price</MenuItem>
                  <MenuItem value="cost">Cost</MenuItem>
                  <MenuItem value="loadedCost">Loaded Cost</MenuItem>
                  <MenuItem value="quantity">Quantity</MenuItem>
                  <MenuItem value="selectedQuantity">Selected Quantity</MenuItem>
                  <MenuItem value="currGroup">Current Group</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small" variant="filled">
                <InputLabel shrink id="option-list-label">Selected Formula</InputLabel>
                <Select
                  value={props.operand?.formula ?? ""}
                  displayEmpty
                  id="option-list-label"
                  onChange={(e) => updateOperand(undefined, undefined, e.target.value, undefined)}
                >
                  <MenuItem value="" disabled>Please select one of the following</MenuItem>
                  <MenuItem value="add">Add</MenuItem>
                  <MenuItem value="subtract">Subtract</MenuItem>
                  <MenuItem value="divide">Divide</MenuItem>
                  <MenuItem value="multiply">Multiply</MenuItem>
                  <MenuItem value="percentageIncrease">Percentage Increase</MenuItem>
                  <MenuItem value="percentageDecrease">Percentage Decrease</MenuItem>
                  <MenuItem value="profitMargin">Profit Margin</MenuItem>
                  <MenuItem value="retailRoundUp">Retail Round Up</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </> 
        ) : undefined 
      }
    </Grid>
  )
} 

function FormulaForm(props) {
  const { newNodeData, setNewNodeData } = useContext(CalculationVisualizerContext);

  const updateNodeData = (selectedFormula, selectedInputAttribute, operand) => {
    const tempNodeData = { 
      ...newNodeData,
      formula: selectedFormula || newNodeData.formula || "",
      inputAttribute: selectedInputAttribute || newNodeData.inputAttribute || "current",
      operand: operand || newNodeData.operand || null,
    };

    setNewNodeData({ ...tempNodeData });
  };

  return (
    <Grid item xs={12}>
      <Typography variant="h6" style={{ marginTop: "10px" }}>Formula Details</Typography>
      <Grid container spacing={2} style={{ marginTop: "10px", border: "2px solid #00000017", borderRadius: "5px" }}>
        <Grid item xs={12}>
          <FormControl fullWidth size="small" variant="filled">
            <InputLabel shrink id="option-list-label">Selected Formula</InputLabel>
            <Select
              value={newNodeData.formula ?? ""}
              displayEmpty
              id="option-list-label"
              onChange={(e) => updateNodeData(e.target.value, undefined, undefined)}
            >
              <MenuItem value="" disabled>Please select one of the following</MenuItem>
              <MenuItem value="add">Add</MenuItem>
              <MenuItem value="subtract">Subtract</MenuItem>
              <MenuItem value="divide">Divide</MenuItem>
              <MenuItem value="multiply">Multiply</MenuItem>
              <MenuItem value="percentageIncrease">Percentage Increase</MenuItem>
              <MenuItem value="percentageDecrease">Percentage Decrease</MenuItem>
              <MenuItem value="profitMargin">Profit Margin</MenuItem>
              <MenuItem value="retailRoundUp">Retail Round Up</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="h6">Input</Typography>
          <FormControl fullWidth size="small" variant="filled">
            <InputLabel shrink id="option-list-label">Selected Input Attribute</InputLabel>
            <Select
              value={newNodeData.inputAttribute ?? ""}
              displayEmpty
              id="option-list-label"
              onChange={(e) => updateNodeData(undefined, e.target.value, undefined)}
            >
              <MenuItem value="" disabled>Please select one of the following</MenuItem>
              <MenuItem value="current">Current</MenuItem>
              <MenuItem value="price">Price</MenuItem>
              <MenuItem value="cost">Cost</MenuItem>
              <MenuItem value="loadedCost">Loaded Cost</MenuItem>
              <MenuItem value="quantity">Quantity</MenuItem>
              <MenuItem value="selectedQuantity">Selected Quantity</MenuItem>
              <MenuItem value="currGroup">Current Group</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="h6">Operand</Typography>
          <OperandInput 
            operand={{ ...newNodeData.operand }} 
            updateOperand={(newOperand) => updateNodeData(undefined, undefined, newOperand)} 
            attributes={props.attributes} 
            nodeType={'formula'} 
            canDelete={false}
          />
        </Grid>
      </Grid>
    </Grid>
  )
}

const DEFAULT_OPERAND = {value: "", attribute: "", formula: "", inputAttribute: "", type: "manual"};

function ConditionForm(props) {
  const { newNodeData, setNewNodeData } = useContext(CalculationVisualizerContext);

  const updateNodeData = (selectedCondition, operands) => {
    const tempNodeData = { ...newNodeData };

    tempNodeData.condition = {
      ...tempNodeData.condition,
      operator: selectedCondition || tempNodeData.condition?.operator || "",
      operands: operands || tempNodeData.condition?.operands || [],
    };

    setNewNodeData({ ...tempNodeData });
  };

  useEffect(() => {
    if (!newNodeData.condition) {
      updateNodeData(undefined, [DEFAULT_OPERAND]);
    }
  }, [])

  return (
    <Grid item xs={12}>
      <Typography variant="h6" style={{ marginTop: "10px" }}>Condition Details</Typography>
      <Grid container spacing={2} style={{ marginTop: "10px", border: "2px solid #00000017", borderRadius: "5px" }}>
        <Grid item xs={12}>
          <FormControl fullWidth size="small" variant="filled">
            <InputLabel shrink id="option-list-label">Selected Condition</InputLabel>
            <Select
              value={newNodeData.condition?.operator || ""}
              displayEmpty
              id="option-list-label"
              onChange={(e) => updateNodeData(e.target.value)}
            >
              <MenuItem value="" disabled>Please select one of the following</MenuItem>
              <MenuItem value="find">Find</MenuItem>
              <MenuItem value="<">Less Than</MenuItem>
              <MenuItem value=">">Greater Than</MenuItem>
              <MenuItem value="=">Equals</MenuItem>
              <MenuItem value="!=">Not Equals</MenuItem>
              <MenuItem value="greatestValue">Greatest Value</MenuItem>
              <MenuItem value="lowestValue">Lowest Value</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}> 
          <Typography variant="h6" style={{ marginTop: "10px" }}>Operand List (In Order)</Typography>
          <div style={{ padding: "0px 10px", overflowX: "hidden", overflowY: "auto", maxHeight: "400px" }}>
            {newNodeData.condition?.operands?.length && newNodeData.condition.operands.map((o, i) => {
              return (
                <OperandInput 
                  key={'operand-' + i} 
                  operand={{ ...o }} 
                  updateOperand={(newOperand) => {
                    const newOperandList = [...newNodeData.condition?.operands];
                    newOperandList.splice(i, 1, newOperand);
                    updateNodeData(undefined, newOperandList);
                  }} 
                  deleteOperand={() => {
                    const newOperandList = [...newNodeData.condition?.operands];
                    newOperandList.splice(i, 1);
                    updateNodeData(undefined, newOperandList);
                  }}
                  canDelete={newNodeData.condition?.operands?.length > 1}
                  attributes={props.attributes} 
                  nodeType={'condition'} 
                />
              )
            })}
          </div>
          <Button 
            variant="contained" 
            fullWidth 
            style={{ marginTop: "10px" }} 
            onClick={() => updateNodeData(undefined, [...newNodeData.condition?.operands, DEFAULT_OPERAND])}
          >
            Add Operand
          </Button>
        </Grid>
      </Grid>
    </Grid>
  )
}

export default function NewNodeModal (props) {
  const { graph, graphSource, setRefreshGraph, newNodeData, setNewNodeData, isNewNodeModalOpen, setIsNewNodeModalOpen } = useContext(CalculationVisualizerContext);
  const [attributes, setAttributes] = useState([]);

  const updateNodeData = (label, selectedGroup, selectedNodeType) => {
    const tempNodeData = { ...newNodeData };

    if (selectedNodeType && selectedNodeType !== tempNodeData.type) {
      delete tempNodeData.formula;
      delete tempNodeData.attribute;
      delete tempNodeData.inputAttribute;

      delete tempNodeData.condition;
    }

    tempNodeData.label = label ?? tempNodeData.label ?? "";
    tempNodeData.group = selectedGroup || tempNodeData.group || "";
    tempNodeData.nodeType = selectedNodeType || tempNodeData.nodeType || "formula";

    setNewNodeData({ ...tempNodeData });
  };

  const addNewNode = async () => {
    const newNode = {
      group: newNodeData.group,
      label: newNodeData.label,
    }

    if (newNodeData.nodeType === "formula") {
      newNode.formula = newNodeData.formula;
      newNode.value = newNodeData.operand.value ? Number(newNodeData.operand.value) : undefined;
      newNode.attribute = newNodeData.operand.attribute;
      newNode.inputAttribute = newNodeData.operand.inputAttribute;
      newNode.next = newNodeData.next;
    } else {
      const operands = newNodeData.condition.operands?.map(o => {
        const value = o.value && !isNaN(Number(o.value)) ? Number(o.value) : o.value || undefined;

        return {
          value,
          attribute: o.attribute,
          inputAttribute: o.inputAttribute,
          formula: o.formula,
          next: newNodeData.next,
        }
      });

      newNode.condition = {
        operator: newNodeData.condition.operator,
        default: {
          next: newNodeData.next,
        },
        operands: [ ...operands ],
      };
    }

    const prevNode = graph[newNodeData.prev];

    if (prevNode.condition && newNodeData.identifier !== null) {
      if (prevNode.condition.default.next === newNodeData.next && newNodeData.identifier === 'default') prevNode.condition.default.next = newNodeData.id;
      
      prevNode.condition.operands.forEach((o, i) => {
        if (o.next === newNodeData.next && (i + 2) === Number(newNodeData.identifier)) prevNode.condition.operands[i].next = newNodeData.id;
      });
    } else {
      prevNode.next = newNodeData.id;
    }

    // Edits persist to the loaded saved graph; templates are read-only
    // (the editor never opens this modal for them).
    if (graphSource?.type !== 'saved') return;

    await updateCalcGraph(graphSource.id, {
      ...graph,
      [newNodeData.prev]: prevNode,
      [newNodeData.id]: newNode,
    });

    setRefreshGraph(true);
    setIsNewNodeModalOpen(false);
    setNewNodeData(null);
  }

  useEffect(() => {
    async function getData() {
      await getOptionAttributes().then(result => {
        setAttributes(result.data.attributes);
      }).catch(err => {
        setAttributes([]);
      });
    }

    getData();

    updateNodeData(undefined, graph[newNodeData?.prev]?.group || "", "formula");
  }, []);

  return (
    <Dialog open={isNewNodeModalOpen} onClose={() => setIsNewNodeModalOpen(false)} maxWidth="sm" fullWidth>
      <Card>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h5">
                New Node
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Node Label"
                fullWidth
                size="small"
                value={newNodeData.label ?? "" }
                onChange={(e) => updateNodeData(e.target.value, undefined, undefined)}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth size="small" variant="filled">
                <InputLabel shrink id="option-list-label">Selected Group</InputLabel>
                <Select
                  value={newNodeData.group ?? ""}
                  displayEmpty
                  id="option-list-label"
                  onChange={(e) => updateNodeData(undefined, e.target.value, undefined)}
                >
                  <MenuItem value="" disabled>Please select one of the following</MenuItem>
                  <MenuItem value="cost">Cost</MenuItem>
                  <MenuItem value="loadedCost">Loaded Cost</MenuItem>
                  <MenuItem value="price">Price</MenuItem>
                  <MenuItem value="quantity">Quantity</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth size="small" variant="filled">
                <InputLabel shrink id="option-list-label">Selected Node Type</InputLabel>
                <Select
                  value={newNodeData.nodeType ?? ""}
                  displayEmpty
                  id="option-list-label"
                  onChange={(e) => updateNodeData(undefined, undefined, e.target.value)}
                >
                  <MenuItem value="" disabled>Please select one of the following</MenuItem>
                  <MenuItem value="formula">Formula</MenuItem>
                  <MenuItem value="condition">Condition</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {newNodeData.nodeType === 'formula' ? (
              <FormulaForm attributes={attributes} />
            ) : (
              <ConditionForm attributes={attributes} />
            )}

            <Grid item xs={12}>
              <Button onClick={() => addNewNode()} variant="contained" style={{ marginTop: "20px", backgroundColor: "#142E3E", color: "#28C4FC" }}>
                Add Node
              </Button>
            </Grid>
          </Grid> 
        </CardContent>
      </Card>
    </Dialog>
  );
}